import React, { ReactNode } from 'react';
import { usePageOwner, useShareUrls } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useTranslation } from 'react-i18next';
import { filterByTypename } from '../../utils/graphql';
import { ShareContextProvider } from '../../context/ShareContext';

interface TimelineShareProps {
  children: ReactNode;
  shareUrl: string;
  description?: string | null;
}

export function TimelineShare({
  children,
  shareUrl,
  description,
}: TimelineShareProps) {
  const { isPageOwner } = usePageOwner();
  const { data: basePageData } = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });

  const charities = filterByTypename(
    basePageData?.page?.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  const emailMapper = (url: string) => {
    let emailMessage: string;
    if (isPageOwner) {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailPageOwner', {
            charityName,
          })}${url}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailPageOwner', {
            charityName,
            emailUrl: url,
          }));
    } else {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailNonPageOwner', {
            name: basePageData?.page?.owner?.name,
            charityName,
          })}${url}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailNonPageOwner', {
            name: basePageData?.page?.owner?.name,
            charityName,
            emailUrl: url,
          }));
    }
    return {
      subject:
        basePageData?.page?.title ??
        (t('common.defaultEmailSubject') as string),
      body: `${t('common.greeting')},\n\n${emailMessage}\n\n${t(
        'common.thankYou',
      )},\n\n${isPageOwner ? basePageData?.page?.owner?.name : ''}`,
    };
  };

  const whatsAppMapper = (url: string) => {
    const whatsappAndSmsMessage = isPageOwner
      ? t('fundraising.shareWhatsappSMSPageOwner', {
          charityName,
        })
      : t('fundraising.shareWhatsappSMSNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
        });

    return `${whatsappAndSmsMessage} ${url}`;
  };

  const smsMapper = (url: string) => {
    const whatsappAndSmsMessage = isPageOwner
      ? t('fundraising.shareWhatsappSMSPageOwner', {
          charityName,
        })
      : t('fundraising.shareWhatsappSMSNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
        });

    return `${whatsappAndSmsMessage} ${url}`;
  };

  const twitterMapper = () => {
    return isPageOwner
      ? t('fundraising.shareTwitterPageOwner', {
          charityName,
        })
      : t('fundraising.shareTwitterNonPageOwner', {
          name: basePageData?.page?.owner?.name,
          charityName,
        });
  };

  const shareItems = useShareUrls(
    {
      email: emailMapper,
      sms: smsMapper,
      twitter: twitterMapper,
      whatsApp: whatsAppMapper,
    },
    shareUrl,
  );

  return (
    <ShareContextProvider value={{ shareItems }}>
      {children}
    </ShareContextProvider>
  );
}
